export interface CallResponse {
  success: boolean;
  data?: any;
  error?: string;
  status?: number;
}

export interface UserProfile {
  user?: {
    address?: {
      addressLine?: string;
      cityName: string;
      countryName: string;
      postalCode?: string;
      stateName?: string;
    };
    currency?: string;
    email?: string;
    firstName?: string;
    id?: string;
    language?: string;
    lastName?: string;
    locale?: string;
    phoneNumber?: string;
  };
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  address?: {
    addressLine?: string;
    cityName: string;
    countryName: string;
    postalCode?: string;
    stateName?: string;
  };
  currency?: string;
  language?: string;
}

export interface GeoCode {
  latitude: number;
  longitude: number;
}

export interface Location {
  geoCodeDTO?: GeoCode;
  geoCode?: GeoCode;
  iataCode: string;
  localDateTime: string;
  name: string;
  timeZoneName: string;
}

export interface Address {
  cityName: string;
  countryCode: string;
  state?: string;
  line?: string;
  stateCode?: string;
  zip?: string;
}

export interface AirportLocation {
  airportAddress: {
    cityName: string;
    countryCode: string;
    state: string;
  };
  name: string;
  subtype: string;
}

export interface FlightTiming {
  baggageClaim: string;
  checkInEndTime: string;
  checkin: string;
  gate: string;
  iataCode: string;
  localDateTime: string;
  localEstimatedDateTime: string;
  terminal: string;
  timeZone: string;
}

export interface FlightDesignator {
  carrierCode: string;
  flightNumber: string;
  operationalSuffix: string;
}

export interface CarrierName {
  name: string;
}

export interface Carrier {
  carrier: CarrierName;
  flightDesignator: FlightDesignator;
}

export interface Seat {
  count: number;
  number: string;
  row: string;
  size: string;
}

export interface Air {
  airlineRecloc: string;
  arrival: FlightTiming;
  departure: FlightTiming;
  arrivalAirportLocation: AirportLocation;
  departureAirportLocation: AirportLocation;
  bookingClass: { cabin: { category: string } };
  carrierCode: string;
  confirmationNumbers: Array<{ isDownloadable: boolean; ticket: string }>;
  connectionTime: string;
  disruptionStatus: string;
  frequentFlyers: Array<{ frequentFlyerNumber: string }>;
  marketing: Carrier;
  operating: Carrier;
  number: string;
  seat: Seat;
  segmentStatus: string;
  suffix: string;
}

export interface Car {
  carModel: string;
  confirmationNumber: string;
  dropoffLocation: Location;
  dropoffTime: string;
  pickupLocation: Location;
  pickupTime: string;
  providerCode: string;
  providerName: string;
}

export interface Hotel {
  address: Address;
  chainCode: string;
  checkInDate: string;
  checkOutDate: string;
  cityCode: string;
  contact: { email: string; phone: string };
  guests: { adults: number; childrenAges: number[] };
  latitude: number;
  longitude: number;
  media: string;
  name: string;
  rateCode: string;
  rating: string;
  room: { roomType: string };
  roomQuantity: number;
  timezone: string;
}

export interface Rail {
  arrivalDateAndTime: string;
  arrivalStationLocation: string;
  arrivalTrack: string;
  departureDateAndTime: string;
  departureStationLocation: string;
  departureTrack: string;
  identifier: string;
  railServiceProvider: { code: string; name: string };
}

export interface Transfer {
  end: { address: Address; dateTime: string; googlePlaceId: string; lfiCode: string; locationCode: string; name: string; uicCode: string };
  start: { address: Address; dateTime: string; googlePlaceId: string; lfiCode: string; locationCode: string; name: string; uicCode: string };
  methodsOfPaymentAccepted: string[];
  transferType: string;
  serviceProvider: {
    code: string;
    name: string;
    logoUrl: string;
    termsUrl: string;
    contacts: { address: Address; email: string; phoneNumber: string };
  };
  vehicle: {
    category: string;
    code: string;
    description: string;
    imageURL: string;
    baggages: Array<{ count: number; size: string }>;
    seats: Seat[];
  };
}

export interface Product {
  air?: Air;
  car?: Car;
  hotel?: Hotel;
  rail?: Rail;
  transfer?: Transfer;
}

export interface Trip {
  end?: Location;
  start?: Location;
  numberOfPassengers: number;
  origin?: string;
  products: Product[];
  reference: string;
  travelServiceLevel: number;
  year?: string;
}

export interface GeoCode {
  latitude: number;
  longitude: number;
}

export interface CityData {
  type: string;
  subType: string;
  name: string;
  iataCode: string;
  address: Address;
  geoCode: GeoCode;
}

export interface CitiesResponse {
  data: CityData[];
}

export interface AirportInformations {
  imageUrl: string;
  airportName: string;
  cityName: string;
  airportCode: string;
  countryName: string;
  cityCode: string;
  countryCode: string;
}

export enum ImageFormat {
  CUSTOM1 = 'custom1', // 600 x 400
  CUSTOM2 = 'custom2', // 900 x 600
  CUSTOM3 = 'custom3', // 1200 x 800
  CUSTOM4 = 'custom4', // 1500 x 1000
  CUSTOM5 = 'custom5', // 1800 x 1200
  CUSTOM6 = 'custom6', // 2100 x 1400
  CUSTOM7 = 'custom7', // 2400 x 1600
  HERO = 'hero', // 1600 x 500
  BILLBOARD = 'billboard', // 970 x 250
  PORTRAIT = 'portrait', // 300 x 1050
  SLIDER = 'slider', // 970 x 550
  LARGE_RECTANGLE = 'largeRectangle', // 336 x 280
  MEDIUM_RECTANGLE = 'mediumRectangle', // 300 x 250
  SMALL_RECTANGLE = 'smallRectangle', // 180 x 150
  RECTANGLE = 'rectangle', // 300 x 100
  WIDE_SKYSCRAPER = 'wideSkyscraper', // 160 x 600
  SKYSCRAPER = 'skyscraper', // 120 x 600
  LARGE_LEADERBOARD = 'largeLeaderboard', // 970 x 90
  LEADERBOARD = 'leaderboard', // 728 x 90
  HALF_PAGE = 'halfPage', // 300 x 600
  LARGE_MOBILE = 'largeMobile', // 320 x 100
  MOBILE = 'mobile', // 320 x 50
  SQUARE = 'square', // 250 x 250
  SMALL_SQUARE = 'smallSquare', // 200 x 200
  MAIN_BANNER = 'mainBanner', // 468 x 60
  HALF_BANNER = 'halfBanner', // 234 x 60
  VERTICAL_BANNER = 'verticalBanner' // 120 x 240
}
